import React, { useState } from "react";

const points = (n) => n == null ? "Non calculable" : n.toLocaleString("fr-FR", { maximumFractionDigits: 2 });
const name = (med) => med.replace(/_/g, " ");
const cell = { padding: "8px 10px", borderBottom: "1px solid #d1d5db", textAlign: "right" };
const count = (counts) => counts.semaine + counts.samedi + counts.dimancheFerie;
const split = (counts) => `${counts.semaine} / ${counts.samedi} / ${counts.dimancheFerie}`;
const nightsCell = (total, external, pr2, type) => <td style={{ ...cell, whiteSpace: "nowrap" }}
  aria-label={`${type} : ${count(total)} ; total semaine/samedi/dimanche-férié : ${split(total)} ; externes : ${split(external)} ; PR2 : ${split(pr2)}`}>
  <strong>{count(total)}</strong> <span>({split(total)})</span>
  <small style={{ display: "block" }}>Externes : {split(external)}</small>
  <small style={{ display: "block" }}>PR2 : {split(pr2)}</small>
</td>;

export default function Pr2EquilibrageResult({ result, jours, warnings, onRestart, onSave,
  choices = {}, onChoiceChange, onRelaunch, pending = false, busy = false, persistenceError = "" }) {
  const [sortBy, setSortBy] = useState("points");
  const bilanTrie = [...result.detailParMedecin].sort((a, b) =>
    (sortBy === "gardes" ? count(b.nuitsGarde) - count(a.nuitsGarde) : 0) ||
    b.total - a.total || a.med.localeCompare(b.med, "fr"));
  const choiceControl = (med) => <div style={{ minWidth: 180, maxWidth: 240, textAlign: "left" }}>
    <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <input type="checkbox" checked={choices[med]?.excluded === true} readOnly disabled={busy || !onChoiceChange}
        aria-label={`Écarter ${name(med)} de cette génération`}
        onClick={() => onChoiceChange(med, { excluded: choices[med]?.excluded !== true })} />
      Écarter de cette génération
    </label>
    <input type="text" aria-label={`Motif facultatif pour ${name(med)}`} placeholder="Motif facultatif"
      value={choices[med]?.motif || ""} maxLength={300} disabled={busy || !onChoiceChange}
      onChange={(e) => onChoiceChange(med, { motif: e.target.value })}
      style={{ width: "100%", boxSizing: "border-box", marginTop: 6, padding: 6 }} />
  </div>;
  return (
    <section aria-labelledby="pr2-equilibrage-title" style={{ marginTop: 24, borderTop: "2px solid #0f766e", paddingTop: 16 }}>
      <h3 id="pr2-equilibrage-title">PR2 : Équilibrage de la permanence des soins</h3>
      <p><strong>{result.joursAttribues.length} / {jours.length} jour(s) attribué(s) par équilibrage.</strong></p>
      {warnings.length > 0 && <div role="status" style={{ color: "#854d0e", background: "#fef9c3", padding: 12 }}>
        <strong>Calcul partiel : données incomplètes.</strong>
        <ul>{warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
        <p>Les données manquantes ne sont pas croisées. Des conflits de date ou d’écart peuvent rester indétectés.
          Relancez la vérification lorsque les listes seront disponibles. Les décomptes sont des planchers.</p>
      </div>}
      <p>Cible théorique initiale : <strong>{points(result.cible)} points</strong>.
        {" "}Moyenne finale : <strong>{points(result.moyenne)} points</strong>.</p>
      <p>Maximum : <strong>{points(result.maximum)}</strong> ; minimum : <strong>{points(result.minimum)}</strong> ;
        {" "}écart : <strong>{points(result.ecartCharges)} points</strong>.</p>
      <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 12 }}>
        <label>Trier par <select aria-label="Trier le bilan" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
          <option value="points">Charge en points</option><option value="gardes">Nombre de nuits de garde</option>
        </select></label>
        <button type="button" onClick={onRelaunch} disabled={busy || !onRelaunch}>{busy ? "Calcul en cours…" : "Relancer"}</button>
        {pending && <span role="status">Choix modifiés : relance nécessaire.</span>}
      </div>
      {persistenceError && <p role="alert">{persistenceError}</p>}
      {onChoiceChange && !persistenceError && <p style={{ fontSize: 13, color: "#4b5563" }}>
        Exclusions conservées sur ce navigateur, pour ce compte et ce mois.
      </p>}
      <div style={{ overflowX: "auto" }}>
        <table aria-label="Bilan des charges PR2" style={{ borderCollapse: "collapse", width: "100%", minWidth: 1050, fontSize: 13 }}>
          <thead>
            <tr><th scope="col" style={cell}>Médecin</th>
              {["Nuits de garde", "Nuits d’astreinte"].map((h) => <th key={h} scope="col" style={cell}>
                {h}<br /><small>Sem. / Sam. / Dim.-F.</small></th>)}
              {["Charge en points", "Écart à la moyenne", "Plafond PR2", "Choix du responsable"].map((h) =>
                <th key={h} scope="col" style={cell}>{h}</th>)}
            </tr>
          </thead>
          <tbody>{bilanTrie.map((d) => <tr key={d.med} style={d.ecartMoyenne >= 2 ? { background: "#fff4d6" } : undefined}>
            <th scope="row" style={{ ...cell, textAlign: "left" }}>{name(d.med)}
              {d.ecartMoyenne >= 2 && <small style={{ display: "block", color: "#704600", fontWeight: 400 }}>Moyenne + 2 pts ou plus</small>}
            </th>
            {nightsCell(d.nuitsGarde, d.decompteExterne.gardes, d.decomptePr2.gardes, "Gardes")}
            {nightsCell(d.nuitsAstreinte, d.decompteExterne.astreintes, d.decomptePr2.astreintes, "Astreintes")}
            <td style={cell}><strong>{points(d.total)}</strong>
              <small style={{ display: "block" }}>Externes : {points(d.externe)}</small>
              <small style={{ display: "block" }}>PR2 : {points(d.pr2)}</small></td>
            {[d.ecartMoyenne, d.plafond].map((n, i) => <td key={i} style={cell}>{points(n)}</td>)}
            <td style={cell}>{choiceControl(d.med)}</td>
          </tr>)}</tbody>
        </table>
      </div>
      {result.exclusions.length > 0 && <div role="status">
        <h4>Médecins non participants</h4>
        <ul>{result.exclusions.map((e) => <li key={e.med} style={{ marginBottom: 12 }}>
          {name(e.med)} : {e.raison}{e.motif && <span> Motif : {e.motif}</span>}
          {e.cause === "responsable" && choiceControl(e.med)}
        </li>)}</ul>
      </div>}
      <h4>Relâchements d’écart</h4>
      {[5, 4, 3].map((gap) => {
        const attributions = result.relachements.filter((j) => j.ecartNecessaire === gap);
        return <section key={gap} aria-labelledby={`pr2-ecart-${gap}-jours`}>
          <h4 id={`pr2-ecart-${gap}-jours`}>Écart de {gap} jours : {attributions.length} attribution(s)</h4>
          {attributions.length === 0 ? <p>Aucune attribution à {gap} jours.</p> :
            <ul>{attributions.map((j) => <li key={j.date}>{j.date} : {name(j.med)}, écart retenu {gap} jours ;
              {" "}charge {points(j.avant)} → {points(j.apres)} points
              {j.etape === "au_dessus_cible" ? " (complément à la cible ou au-dessus)" : ""}.</li>)}</ul>}
        </section>;
      })}
      <h4>Jours non pourvus</h4>
      {result.joursVides.length === 0 ? <p>Tous les jours sont pourvus.</p> : <ul>
        {result.joursVides.map((j) => <li key={j.date}>
          <strong>{j.date}</strong> : {j.raisons.join(" ; ")}.
          {j.details.length > 0 && <details><summary>Détail par médecin</summary>
            {j.details.map((d) => <p key={d.med}>{name(d.med)} : {d.raisons.join(" ; ")}.</p>)}
          </details>}
        </li>)}
      </ul>}
      <h4>Liste proposée</h4>
      <div style={{ overflowX: "auto" }}>
        <table aria-label="Liste PR2 équilibrée" style={{ borderCollapse: "collapse", width: "100%" }}>
          <thead><tr>{["Date", "Médecin", "Étape", "Charge avant → après"].map((h) => <th key={h} style={cell}>{h}</th>)}</tr></thead>
          <tbody>{jours.map((date) => {
            const j = result.journal.find((entry) => entry.date === date);
            return <tr key={date}><th scope="row" style={cell}>{date}</th>
              <td style={cell}>{j ? name(j.med) : "Non pourvu"}</td>
              <td style={cell}>{j ? j.etape === "sous_cible" ? `Sous cible, écart ${j.ecart} j` : "À la cible ou au-dessus" : ""}</td>
              <td style={cell}>{j ? `${points(j.avant)} → ${points(j.apres)}` : ""}</td></tr>;
          })}</tbody>
        </table>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginTop: 16 }}>
        <button type="button" onClick={onRestart} disabled={busy}>Recommencer</button>
        <button type="button" onClick={onSave} disabled={busy || pending}>Enregistrer et ouvrir la liste PR2</button>
      </div>
    </section>
  );
}
